import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, SafeAreaView, KeyboardAvoidingView, FlatList, TouchableOpacity, Platform } from 'react-native'
import { Modal, Text, Button, Card, Spinner, Icon, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewProductCategory, updateProductCategory, saveProductCategoriesOrder, deleteProductCategory } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { HintView } from '../../components/HintView'
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { productCategoriesListAtom, productsDictAtom } from '../../global/AtomsProvider'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { reorderProductCategories } from '../../utils/productListHelpers'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

export default function ProductCategoriesScreen({ navigation }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context] = useContext(UserContext)
  const db = context.db
  const productCategoriesList = useAtomValue(productCategoriesListAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const postHog = usePostHog()
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [nameValue, setNameValue] = useState('')
  const [moveCategory, setMoveCategory] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isReordering, setIsReordering] = useState(false)
  // ATL-1892 — a failed reorder is shown on the list itself; `saveError` renders only inside the modals.
  const [reorderFailed, setReorderFailed] = useState(false)
  const [saveError, setSaveError] = useState()

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            setSelectedIndex(null)
            setNameValue('')

            setSaveError(null)
            setEditModalVisible(true)
          }}
          iconName='plus-outline'
          testID='category-add-btn'
        />
      ),
    })
  }, [])

  // Log category detail for E2E testing when edit modal opens
  useEffect(() => {
    if (editModalVisible && selectedIndex !== null && productCategoriesList[selectedIndex]) {
      const category = productCategoriesList[selectedIndex];
      console.log('[CATEGORY_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: category.id,
        name: category.name,
        order: category.order,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [editModalVisible, selectedIndex])

  const saveCategory = async () => {
    // Use current list index as order (source of truth after reordering)
    const order = selectedIndex != null ? selectedIndex : productCategoriesList.length
    const categoryInfo = {
      name: nameValue,
      order,
      organizationId: context.user.organizationId,
      lastChangeBy: context.user.uid,
      lastChangeAt: new Date().toISOString(),
      dailyStatID: getDailyStatID(null, new Date()),
      lastChangeStore: context.user.store.id,
    }
    setIsSaving(true)
    setSaveError(null)
    try {
      if (selectedIndex == null) {
        await saveNewProductCategory(db, categoryInfo)
        sendFBAnalyticsEvent('new_product_category')
        postHog.capture('product_category_created')
      } else {
        await updateProductCategory(productCategoriesList[selectedIndex].ref, categoryInfo)
        sendFBAnalyticsEvent('update_product_category')
        postHog.capture('product_category_updated')
      }
      setEditModalVisible(false)
    } catch (err) {
      Sentry.captureException(err)
      setSaveError(err?.message || String(err))
    } finally {
      setIsSaving(false)
    }
  }

  const deleteCategory = async () => {
    if (moveCategory) {
      setIsLoading(true) 
      setSaveError(null)
      var productsToMove = []
      Object.values(productsDict).forEach(product => {
        if (product.categoryID === productCategoriesList[selectedIndex].id) {
          productsToMove.push(product)
        }
      })
      try {
        await deleteProductCategory(productCategoriesList[selectedIndex].ref, productsToMove, moveCategory, context.user.uid)
        setDeleteModalVisible(false)
        setIsLoading(false)
      } catch(err) {
        Sentry.captureException(err)
        setIsLoading(false)
        setSaveError(err)
      }
    } else {
      setSaveError(t('notAllFieldsFilled'))
    }
  }

  const renderItem = ({item, index}) => {
    return (
      <TouchableOpacity
        style={[styles.item, {backgroundColor: theme['background-basic-color-1']}]}
        key={index.toString()}
        testID={`category-item-${index}`}
        dataSet={{ testid: `category-item-${index}` }}
        onPress={() => {
          setSelectedIndex(index)
          setNameValue(productCategoriesList[index].name)

          setSaveError(null)
          setEditModalVisible(true)
        }}
      >
        <TouchableOpacity style={styles.button} onPress={() => {moveRowDown(index)}} disabled={isReordering} testID={`category-down-${index}`} {...(Platform.OS === 'web' ? { dataSet: { testid: `category-down-${index}` } } : {})}>
          <Icon name='arrow-downward-outline' style={{width: 24, height: 24}} fill={theme['color-info-500']}/>
        </TouchableOpacity>
        <TouchableOpacity style={styles.button} onPress={() => {moveRowUp(index)}} disabled={isReordering} testID={`category-up-${index}`} {...(Platform.OS === 'web' ? { dataSet: { testid: `category-up-${index}` } } : {})}>
          <Icon name='arrow-upward-outline' style={{width: 24, height: 24}} fill={theme['color-info-500']}/>
        </TouchableOpacity>
        <Text style={styles.itemTitle}>{item.text}</Text>
        <Button style={styles.button} appearance='ghost' status='info' accessoryLeft={EditIcon} onPress={() => {
          setSelectedIndex(index)
          setNameValue(productCategoriesList[index].name)

          setSaveError(null)
          setEditModalVisible(true)}}
        />
      </TouchableOpacity>
    )
  }

  // ATL-1892 — every row of the reordered list is written, not only the rows whose `order` looks
  // changed: the values here are what this till READ, and a Supabase-read till's stale values cannot
  // vouch for Firestore's (see reorderProductCategories). All of them go in ONE Firestore batch
  // (saveProductCategoriesOrder), so a reorder lands whole or not at all, and `isReordering` covers
  // the commit and the mirror writes. The list re-renders from productCategoriesListAtom when the
  // categories listener / Supabase refetch delivers the new orders.
  //
  // A rejected batch changed nothing (e.g. NOT_FOUND: the list holds a category whose Firestore
  // document is gone), so the merchant is told exactly that on the list, instead of the arrow
  // silently doing nothing. Sentry gets the row count and Firestore's error code, never names. The
  // banner is cleared when the next move starts, so a move that then succeeds leaves it cleared.
  const moveRow = async (index, direction) => {
    if (isReordering) return
    const move = reorderProductCategories(productCategoriesList, index, direction)
    if (!move) return
    setReorderFailed(false)
    setIsReordering(true)
    try {
      await saveProductCategoriesOrder(move.writes.map(({ category, order }) => ({ ref: category.ref, order })))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: 'categoryReorder', atl: 'ATL-1892' },
        extra: { rowCount: move.writes.length, firestoreErrorCode: err?.code },
      })
      setReorderFailed(true)
    } finally {
      setIsReordering(false)
    }
  }

  const moveRowDown = (index) => moveRow(index, 1)

  const moveRowUp = (index) => moveRow(index, -1)

  const ArrowDownIcon = (style) => (
    <Icon {...style} name='arrow-downward-outline'/>
  );
  const ArrowUpIcon = (style) => (
    <Icon {...style} name='arrow-upward-outline'/>
  );
  const EditIcon = (style) => (
    <Icon {...style} name='edit-outline'/>
  );

  const footerItem = () => {
    return (
      <View>
        <HintView 
          text={t('settings.categorySortHint')}
          iconName='alert-circle-outline' style={{marginVertical: 10}}
        />
        <View style={{height:50}}></View>
      </View>
    )
  }
  
  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      {reorderFailed && (
        <Text
          status='danger'
          style={styles.reorderError}
          testID='category-reorder-error'
          {...(Platform.OS === 'web' ? { dataSet: { testid: 'category-reorder-error' } } : {})}
        >
          {t('settings.categoryReorderFailed')}
        </Text>
      )}
      <FlatList 
        data={productCategoriesList}
        keyExtractor={(item, index) => item.id}
        renderItem={renderItem}
        style={styles.list} 
        keyboardDismissMode='on-drag'
        keyboardShouldPersistTaps='never'
        ListFooterComponent={footerItem}
      />
      <Modal style={styles.modal} visible={editModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => setEditModalVisible(false)}>
        <Card style={styles.modalContainer}>
          {selectedIndex == null ? (<Text>{t('settings.newCategory')}</Text>) : (<Text appearance='hint' style={{textAlign: 'center', marginBottom: 10}}>{productCategoriesList[selectedIndex]?.id}</Text>)}
          <Text category='label' style={styles.label}>
            {t('settings.name')}
          </Text>
          <StyledTextInput
            value={nameValue}
            onChangeText={setNameValue}
            style={styles.spacedItem}
            returnKeyType='done'
          />
          {saveError != null && (
            <Text style={styles.messageText} status='danger'>{saveError}</Text>
          )}
          {isSaving ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <>
              <Button style={styles.spacedItem} onPress={saveCategory}>
                {t('save')}
              </Button>
              {selectedIndex != null && (<Button status='danger' style={styles.spacedItem} onPress={() => {
                setEditModalVisible(false)
                setDeleteModalVisible(true)
              }}>
                {t('delete')}
              </Button>)}
              <Button status='basic' style={styles.spacedItem} onPress={() => setEditModalVisible(false)}>
                {t('cancel')}
              </Button>
            </>
          )}
        </Card>
      </Modal>

      <Modal style={styles.modal} visible={deleteModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => setDeleteModalVisible(false)}>
        <Card style={styles.modalContainer}>
          <Text category='label' style={styles.label}>
            {t('settings.moveToCategory')}
          </Text>
          <PlatformDependantSelect
            data={productCategoriesList}
            selectedOption={moveCategory}
            onSelect={setMoveCategory}
            style={styles.spacedItem}
          />
          {saveError != '' && (
            <Text style={styles.messageText} status='danger'>{saveError}</Text>
          )} 
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <Button  status='danger' style={styles.spacedItem} onPress={deleteCategory}>
              {t('delete')}
            </Button>
          )}
          <Button status='basic' style={styles.spacedItem} onPress={() => setDeleteModalVisible(false)}>
            {t('cancel')}
          </Button>
        </Card>
      </Modal>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  modal: {
    top: 100,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 300,
    height: '80%',
    padding: 16,
    margin: 32
  },
  spacedItem: {
    marginVertical: 4,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  label: {
    marginVertical: 4
  },
  spinner: {
    alignItems: 'center'
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 2,
    borderRadius: 5,
    height: 56,
  },
  list: {
    paddingHorizontal: 20,
    paddingVertical: 10
  },
  itemTitle: {
    flex: 1
  },
  reorderError: {
    marginHorizontal: 20,
    marginTop: 10,
  }
});
